import { EikoLogo } from '@/components/EikoLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRequestPasswordReset } from '@/hooks/useRequestPasswordReset';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

const forgotPasswordSchema = z.object({
  email: z.string().email('Veuillez entrer un email valide'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, filter: 'blur(4px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95, filter: 'blur(8px)' },
  visible: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
};

const inputClassName = cn(
  'h-11 rounded-lg border-stone-200 bg-white',
  'text-stone-900 placeholder:text-stone-400',
  'transition-all duration-300',
  'focus-visible:border-emerald-500/50',
  'focus-visible:ring-[3px] focus-visible:ring-emerald-500/15',
  'focus-visible:bg-white',
  'hover:border-stone-300',
);

export function ForgotPasswordView() {
  const { requestReset, isLoading, isSuccess } = useRequestPasswordReset();

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const handleSubmit = (data: ForgotPasswordFormData) => {
    requestReset(data.email, {
      onSuccess: () => form.reset(),
    });
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-stone-50">
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full max-w-[420px] mx-4"
      >
        <div
          className={cn(
            'rounded-2xl border border-stone-200 p-8 sm:p-10',
            'bg-white',
            'shadow-sm',
          )}
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col"
          >
            {/* Brand */}
            <motion.div variants={itemVariants} className="mb-8 flex justify-center">
              <EikoLogo className="h-14 text-emerald-600" />
            </motion.div>

            {/* Title */}
            <motion.div variants={itemVariants} className="mb-6 text-center">
              <h2 className="text-lg font-medium text-stone-800">
                Mot de passe oublié ?
              </h2>
              <p className="mt-1.5 text-sm text-stone-500">
                Entrez votre email pour recevoir les instructions de réinitialisation
              </p>
            </motion.div>

            {isSuccess ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col items-center text-center"
              >
                <CheckCircle className="h-12 w-12 text-emerald-500" />
                <p className="mt-4 text-sm font-medium text-stone-900">
                  Si un compte existe pour cet email, un lien de réinitialisation a été envoyé.
                </p>
                <p className="mt-1.5 text-sm text-stone-600">
                  Pensez à vérifier vos spams.
                </p>
                <Link
                  to="/login"
                  className={cn(
                    'mt-6 inline-flex h-11 items-center justify-center rounded-lg px-6 text-sm font-semibold',
                    'bg-emerald-600 text-white',
                    'hover:bg-emerald-700',
                    'transition-all duration-300',
                  )}
                >
                  Retour à la connexion
                </Link>
              </motion.div>
            ) : (
              <>
                <form onSubmit={form.handleSubmit(handleSubmit)}>
                  <div className="grid gap-4">
                    <motion.div variants={itemVariants} className="grid gap-2">
                      <Label htmlFor="email" className="text-stone-600 text-sm">
                        Email
                      </Label>
                      <Input
                        {...form.register('email')}
                        id="email"
                        placeholder="nom@exemple.com"
                        type="email"
                        autoCapitalize="none"
                        autoComplete="email"
                        autoCorrect="off"
                        disabled={isLoading}
                        className={inputClassName}
                      />
                      {form.formState.errors.email && (
                        <p className="text-sm text-red-400">
                          {form.formState.errors.email.message}
                        </p>
                      )}
                    </motion.div>

                    <motion.div variants={itemVariants} className="pt-2">
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className={cn(
                          'relative w-full h-11 rounded-lg text-sm font-semibold',
                          'bg-emerald-600 text-white',
                          'hover:bg-emerald-700',
                          'transition-all duration-300',
                          'disabled:opacity-50',
                        )}
                      >
                        {isLoading ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          'Envoyer les instructions'
                        )}
                      </Button>
                    </motion.div>

                    <motion.div variants={itemVariants} className="text-center pt-2">
                      <Link
                        to="/login"
                        className="text-sm text-stone-400 transition-colors duration-200 hover:text-emerald-600"
                      >
                        Retour à la connexion
                      </Link>
                    </motion.div>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
